import React, { useCallback, useEffect, useState } from 'react';
import { decoyKeyMatches } from '../../lib/decoy';

/**
 * A calculator that calculates.
 *
 * That is not a joke about scope — it is the requirement. A disguise where
 * 7 + 3 does nothing is worse than no disguise at all: the curious person who
 * picked up the phone now knows something is wrong AND has become interested.
 * So this is a working calculator first, and a door second.
 *
 * The door is the sequence, not the result. The member remembers a small
 * calculation — say 7×8 — and typing it opens Arcanum. A wrong sequence is not
 * rejected; it is simply calculated, exactly as a calculator would, so there is
 * nothing to notice and nothing to probe. Chosen over a bare code because it is
 * easier to remember, and because typing it looks like using the app.
 *
 * Deliberately no Arcanum in here: no gold, no serif, no theme tokens. It reads
 * the app's stylesheet, and anything borrowed from it would be a tell.
 */

const KEYS = [
  [{ k: 'C', t: 'fn' }, { k: '±', t: 'fn' }, { k: '%', t: 'fn' }, { k: '÷', t: 'op' }],
  [{ k: '7' }, { k: '8' }, { k: '9' }, { k: '×', t: 'op' }],
  [{ k: '4' }, { k: '5' }, { k: '6' }, { k: '−', t: 'op' }],
  [{ k: '1' }, { k: '2' }, { k: '3' }, { k: '+', t: 'op' }],
  [{ k: '0', wide: true }, { k: ',' }, { k: '=', t: 'op' }],
];

const OPS = { '+': (a, b) => a + b, '−': (a, b) => a - b, '×': (a, b) => a * b, '÷': (a, b) => (b === 0 ? NaN : a / b) };

const toNumber = (s) => parseFloat(String(s).replace(',', '.'));

const format = (n) => {
  if (!Number.isFinite(n)) return 'Fejl';
  // Long decimals look like a bug on a phone; nine significant figures is what
  // a pocket calculator would show.
  const r = Math.abs(n) >= 1e9 ? n.toExponential(4) : String(parseFloat(n.toPrecision(9)));
  return r.replace('.', ',');
};

const Calculator = ({ onOpen }) => {
  const [entry, setEntry] = useState('0');
  const [stored, setStored] = useState(null);
  const [op, setOp] = useState(null);
  const [fresh, setFresh] = useState(true);
  // What has been typed since the last clear or evaluation, in the member's
  // own notation. This is what the door compares against.
  const [seq, setSeq] = useState('');

  const reset = useCallback(() => {
    setEntry('0'); setStored(null); setOp(null); setFresh(true); setSeq('');
  }, []);

  const press = useCallback((k) => {
    if (k === 'C') { reset(); return; }

    if (k === '±') {
      setEntry((e) => (e.startsWith('-') ? e.slice(1) : e === '0' ? e : '-' + e));
      return;
    }

    if (k === '%') {
      setEntry((e) => format(toNumber(e) / 100));
      setFresh(true);
      return;
    }

    if (/[0-9]/.test(k)) {
      setSeq((s) => s + k);
      setEntry((e) => (fresh || e === '0' ? k : e + k));
      setFresh(false);
      return;
    }

    if (k === ',') {
      setSeq((s) => s + ',');
      setEntry((e) => (fresh ? '0,' : e.includes(',') ? e : e + ','));
      setFresh(false);
      return;
    }

    if (k === '=') {
      // The door. Checked before arithmetic so that opening it leaves no
      // trace on the display for the next person to wonder about.
      if (decoyKeyMatches(seq)) { reset(); onOpen(); return; }
      if (op !== null && stored !== null) {
        const result = OPS[op](stored, toNumber(entry));
        setEntry(format(result));
        setStored(null); setOp(null);
      }
      setFresh(true);
      setSeq('');
      return;
    }

    if (OPS[k]) {
      setSeq((s) => s + k);
      if (op !== null && stored !== null && !fresh) {
        const result = OPS[op](stored, toNumber(entry));
        setEntry(format(result));
        setStored(result);
      } else {
        setStored(toNumber(entry));
      }
      setOp(k);
      setFresh(true);
    }
  }, [entry, fresh, op, stored, seq, reset, onOpen]);

  // A physical keyboard, because this also runs on a desktop and a calculator
  // that ignores the number row would be an odd calculator.
  useEffect(() => {
    const onKey = (e) => {
      const map = {
        '*': '×', 'x': '×', '/': '÷', '-': '−', '+': '+',
        'Enter': '=', '=': '=', 'Escape': 'C', 'c': 'C', '.': ',', ',': ',',
      };
      const k = /[0-9]/.test(e.key) ? e.key : map[e.key];
      if (!k) return;
      e.preventDefault();
      press(k);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [press]);

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col"
      style={{
        background: '#1C1C1E',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      data-testid="decoy-calculator"
    >
      <div className="flex-1 flex items-end justify-end px-6 pb-6 min-h-0">
        <div
          className="w-full text-right font-light tabular-nums"
          style={{
            color: '#F2F2F7',
            fontSize: 'clamp(44px, 15vw, 76px)',
            lineHeight: 1.05,
            overflowWrap: 'anywhere',
          }}
          data-testid="decoy-display"
        >
          {entry}
        </div>
      </div>

      <div className="px-4 pb-5" style={{ maxWidth: 520, width: '100%', margin: '0 auto' }}>
        {KEYS.map((row, ri) => (
          <div key={ri} className="grid gap-3 mb-3" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {row.map(({ k, t, wide }) => (
              <button
                key={k}
                type="button"
                onClick={() => press(k)}
                aria-label={k}
                data-testid={`decoy-key-${k}`}
                className="rounded-full select-none active:opacity-70 transition-opacity"
                style={{
                  gridColumn: wide ? 'span 2' : undefined,
                  aspectRatio: wide ? undefined : '1',
                  minHeight: 64,
                  fontSize: 26,
                  fontWeight: 400,
                  color: t === 'fn' ? '#1C1C1E' : '#F2F2F7',
                  background: t === 'op' ? '#C77B3A' : t === 'fn' ? '#A5A5AA' : '#333336',
                  border: 'none',
                }}
              >
                {k}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Calculator;
